import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const PRINCIPLES = [
  {
    number: '01',
    title: 'Penetration Over Loyalty',
    body: 'Brands grow by reaching more buyers, not by making existing customers more loyal. Occasional users are the true growth engine — not heavy users.',
  },
  {
    number: '02',
    title: 'The Double Jeopardy Law',
    body: 'Smaller brands suffer twice — fewer customers who also buy less often. Market share and purchase frequency move in lockstep.',
  },
  {
    number: '03',
    title: 'Light Buyers Are the Majority',
    body: 'Most customers interact with a brand rarely. Heavy users are vocal but represent a small fraction of total growth potential.',
  },
  {
    number: '04',
    title: 'Mental Availability',
    body: 'The brand that comes to mind first when a need arises wins the sale. Reach and salience consistently outperform deep loyalty programmes.',
  },
];

const JOURNALS = [
  {
    id: 1,
    label: 'Ehrenberg-Bass Institute · 2024',
    authors: 'Anesbury, Weismueller & Hawkins (Eureka AI)',
    title: 'Mobile App User Profiles Seldom Differ Across Categories and Countries',
    desc: 'An empirical study of 89 million smartphone users across Indonesia, Thailand and Hong Kong, examining whether competing mobile apps attract meaningfully different user profiles.',
    summary: [
      'Analysed 320 mobile applications across 36 categories tracked over four monthly periods — one of the largest mobile panel studies of its kind.',
      'Competing apps within the same category attract near-identical user profiles. A user\'s demographic characteristics (age, gender, location) resemble the average app user far more than they resemble any specific category user.',
      'This pattern holds consistently across countries and categories — from ride hailing to food delivery to shopping — confirming that Sharp\'s empirical generalisations extend to the mobile app world.',
      'Implication for practitioners: demographic segmentation targeting specific apps is unlikely to reach audiences meaningfully different from those of competing apps. Reach and mental availability matter more than narrowly targeted demographic strategies.',
    ],
    pdf: '/journals/journal-1.pdf',
  },
  {
    id: 2,
    label: 'Australasian Marketing Journal · 2025',
    authors: 'Stocchi, Hawkins (Eureka AI) & Tanusondjaja',
    title: 'The Exceptions Confirming the Rule: Deviations in the Market Performance of Standalone Mobile Apps',
    desc: 'Published in the Australasian Marketing Journal, this study applies the Double Jeopardy Law to standalone mobile apps across three countries and categories, identifying what drives and what explains performance deviations.',
    summary: [
      'Using longitudinal panel data from Hong Kong, Thailand and Indonesia across ride sharing, food delivery and online shopping, the study benchmarks actual app performance against Double Jeopardy predictions.',
      'Most standalone apps conform to the Double Jeopardy Law — their usage rate (stickiness) is proportional to their market penetration, just as in traditional consumer goods markets.',
      'Deviations exist but are explainable: apps that over-perform on stickiness relative to penetration typically hold a defensible niche; those that under-perform often face substitution pressure or poor retention.',
      'The study confirms that known empirical marketing laws — developed over decades in FMCG — apply robustly to digital-first, standalone app markets, giving marketers a principled framework for benchmarking and strategy.',
    ],
    pdf: '/journals/journal-2.pdf',
  },
];

function PdfModal({ journal, onClose }) {
  return (
    <div className="lp-pdf-overlay" onClick={onClose}>
      <div className="lp-pdf-modal" onClick={e => e.stopPropagation()}>
        <div className="lp-pdf-header">
          <span className="lp-pdf-title">{journal.title}</span>
          <button className="lp-pdf-close" onClick={onClose}>✕</button>
        </div>
        <div className="lp-pdf-body">
          <iframe
            src={journal.pdf}
            title={journal.title}
            width="100%"
            height="100%"
            style={{ border: 'none', borderRadius: '0 0 12px 12px' }}
          />
        </div>
      </div>
    </div>
  );
}

export default function LandingView({ onEnter, scrollToBook, onScrolled }) {
  const { user, logout } = useAuth();
  const [activeJournal,   setActiveJournal]   = useState(null);
  const [expandedSummary, setExpandedSummary] = useState(null);

  useEffect(() => {
    if (scrollToBook) {
      const el = document.getElementById('lp-book');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      onScrolled?.();
    }
  }, [scrollToBook]);

  const toggleSummary = (id) =>
    setExpandedSummary(prev => prev === id ? null : id);

  return (
    <div className="lp-root">
      {activeJournal && (
        <PdfModal journal={activeJournal} onClose={() => setActiveJournal(null)} />
      )}

      {/* ── Nav ── */}
      <nav className="lp-nav">
        <img src="/eureka-logo.png" alt="Eureka AI" className="lp-nav-logo" />
        <div className="lp-nav-right">
          <span className="lp-nav-user">{user?.displayName || user?.email}</span>
          <button className="lp-nav-signout" onClick={logout}>Sign out</button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="lp-hero">
        <div className="lp-hero-inner">
          <div className="lp-eyebrow">Omni Growth Platform · Eureka AI</div>
          <h1 className="lp-hero-title">
            How Brands Grow<br />
            <span className="lp-hero-accent">in the Digital Age</span>
          </h1>
          <p className="lp-hero-sub">
            This dashboard is built on the empirical laws of Byron Sharp — translating
            decades of marketing science into actionable intelligence across the mobile
            app categories that shape modern consumer behaviour.
          </p>
          <button className="lp-hero-cta" onClick={onEnter}>
            Explore the Dashboard
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </button>
        </div>
        <div className="lp-hero-glow" />
      </section>

      {/* ── Book ── */}
      <section id="lp-book" className="lp-section">
        <div className="lp-container">
          <div className="lp-book-card">
            <div className="lp-book-cover">
              <img
                src="/book-cover1.png"
                alt="How Brands Grow"
                className="lp-book-cover-img"
                onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
              <div className="lp-book-cover-placeholder" style={{ display: 'none' }}>
                <div className="lp-book-cover-label">How Brands Grow</div>
                <div className="lp-book-cover-author">Byron Sharp</div>
                <div className="lp-book-cover-hint">Add cover to<br />public/book-cover.jpg</div>
              </div>
            </div>

            <div className="lp-book-info">
              <div className="lp-label-tag">The Founding Text</div>
              <h2 className="lp-book-title">How Brands Grow: What Marketers Don't Know</h2>
              <p className="lp-book-byline">Byron Sharp · Oxford University Press · 2010</p>
              <p className="lp-book-body">
                Sharp's landmark work overturned decades of marketing orthodoxy with one
                simple finding: brands grow through mental and physical availability — not
                loyalty programmes, emotional connection, or heavy-user retention.
              </p>
              <p className="lp-book-body">
                Drawing on the Ehrenberg-Bass Institute's empirical research across hundreds
                of categories and countries, the book establishes universal laws of buyer
                behaviour. The Omni Growth Platform surfaces exactly these patterns in
                real-world mobile app behaviour — letting you see Sharp's laws in action,
                market by market, month by month.
              </p>
              <a
                href="https://www.amazon.co.uk/dp/B00I5QZCQI/?bestFormat=true&k=how%20brands%20grow&ref_=nb_sb_ss_w_scx-ent-bk-ww_k0_1_10_de&crid=1G69734N0F1QI&sprefix=how%20brands"
                className="lp-book-amazon"
                target="_blank"
                rel="noopener noreferrer"
                title="Replace with actual Amazon link"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                View on Amazon
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Principles ── */}
      <section className="lp-section lp-section-alt">
        <div className="lp-container">
          <div className="lp-section-hd">
            <div className="lp-eyebrow">The Science</div>
            <h2 className="lp-section-title">Four Laws That Explain Brand Growth</h2>
            <p className="lp-section-sub">Every chart in this dashboard connects back to one of these empirical laws.</p>
          </div>
          <div className="lp-principles-grid">
            {PRINCIPLES.map(p => (
              <div key={p.number} className="lp-principle-card">
                <div className="lp-principle-num">{p.number}</div>
                <h3 className="lp-principle-title">{p.title}</h3>
                <p className="lp-principle-body">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Journals ── */}
      <section className="lp-section">
        <div className="lp-container">
          <div className="lp-section-hd">
            <div className="lp-eyebrow">Eureka AI Research</div>
            <h2 className="lp-section-title">Published Journals</h2>
            <p className="lp-section-sub">Deeper reading from the Eureka AI team — bridging marketing science and real-world data.</p>
          </div>
          <div className="lp-journals-grid">
            {JOURNALS.map(j => {
              const isOpen = expandedSummary === j.id;
              return (
                <div key={j.id} className="lp-journal-card">
                  <div className="lp-journal-thumb">
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                    </svg>
                  </div>

                  <div className="lp-journal-body">
                    <span className="lp-journal-label">{j.label}</span>
                    <h3 className="lp-journal-title">{j.title}</h3>
                    <p className="lp-journal-authors">{j.authors}</p>
                    <p className="lp-journal-desc">{j.desc}</p>

                    {/* Summary expand / collapse */}
                    {isOpen && (
                      <div className="lp-journal-summary">
                        <div className="lp-journal-summary-heading">Key Findings</div>
                        <ul className="lp-journal-summary-list">
                          {j.summary.map((point, i) => (
                            <li key={i}>{point}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="lp-journal-actions">
                      <button
                        className="lp-journal-btn lp-journal-btn-summary"
                        onClick={() => toggleSummary(j.id)}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          {isOpen
                            ? <polyline points="18 15 12 9 6 15"/>
                            : <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></>
                          }
                        </svg>
                        {isOpen ? 'Hide Summary' : 'Show Summary'}
                      </button>

                      <button className="lp-journal-btn" onClick={() => setActiveJournal(j)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                        Read Journal
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="lp-cta-section">
        <div className="lp-container lp-cta-inner">
          <h2 className="lp-cta-title">Ready to explore the data?</h2>
          <p className="lp-cta-sub">Monthly analytics across age, gender, geography, intensity, loyalty, competition and social overlap.</p>
          <button className="lp-cta-btn" onClick={onEnter}>
            Explore the Dashboard
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </button>
        </div>
      </section>

      <div className="lp-footer">© 2026 Eureka AI · Actionable Intelligence at Scale</div>
    </div>
  );
}
